import { useGikSettings } from "@gik/api/acf/gik-settings/gikSettings";
import { InViewport } from "@gik/core/components/InViewport";
import type WordpressSituation from "@gik/core/models/wordpress/WordpressSituation";
import { ErrorPageSection } from "@gik/core/pages/Error/ErrorPageSection";
import bemBlock from "@gik/core/utils/bemBlock";
import withComponentErrorBoundary from "@gik/core/utils/withComponentErrorBoundary";
import { CreateClickedLocation } from "@gik/create/enums/CreateAnalyticsEnums";
import { FeatureStepsSection } from "@gik/marketing/components/FeatureSteps";
import { SituationsOverviewSection } from "@gik/marketing/components/SituationsOverview";
import { EmbeddedVideo } from "@gik/ui-legacy/EmbeddedVideo";
import { PageHeader } from "@gik/ui-legacy/gik/PageHeader";
import { PageSection } from "@gik/ui-legacy/gik/PageSection";
import {
	StartInkindCard,
	StartInkindContent,
} from "@gik/ui-legacy/gik/StartInkindCard";
import { TestimonialsCarouselSection } from "@gik/ui-legacy/gik/TestimonialsCarouselSection";
import { KeepProportions, Wide } from "@gik/ui-legacy/KeepProportions";
import type React from "react";
import { useTranslation } from "react-i18next";
import { MarketingNavSection } from "../../components/MarketingNav/MarketingNavSection";
import { HowItWorksSamplePagesSection } from "./HowItWorksSamplePagesSection";
import { translationKeys } from "./i18n/en";

export type HowItWorksPageServerProps = {
	situations: WordpressSituation[];
};

export type HowItWorksPageProps = {};

function HowItWorksPageComp(): React.ReactElement {
	const bem = bemBlock("how-it-works-page");
	const { t } = useTranslation();
	const { data: gikSettings } = useGikSettings();

	return (
		<article className={bem()}>
			<InViewport height={160}>
				<PageHeader variant="aqua" decoration="foliage">
					<h1>{t(translationKeys.title)}</h1>
				</PageHeader>
			</InViewport>

			<InViewport height={572}>
				<PageSection className={bem("video")}>
					<KeepProportions proportion={Wide}>
						{gikSettings && (
							<EmbeddedVideo
								url={`https://www.youtube.com/watch?v=${gikSettings.acf.how_it_works_video_id}`}
								autoPlay={false}
							/>
						)}
					</KeepProportions>
				</PageSection>
			</InViewport>

			<InViewport height={69}>
				<PageSection noPad>
					<StartInkindContent
						trackingId={CreateClickedLocation.HOW_IT_WORKS_TOP}
					/>
				</PageSection>
			</InViewport>

			<InViewport height={500}>
				<FeatureStepsSection />
			</InViewport>

			<InViewport height={572}>
				<SituationsOverviewSection />
			</InViewport>
			<InViewport height={175}>
				<StartInkindCard trackingId={CreateClickedLocation.HOW_IT_WORKS_HALF} />
			</InViewport>
			<InViewport height={466}>
				<HowItWorksSamplePagesSection />
			</InViewport>
			<InViewport height={394}>
				<TestimonialsCarouselSection />
			</InViewport>
			<InViewport height={131}>
				<PageSection className={bem("start-inkind")}>
					<StartInkindContent
						trackingId={CreateClickedLocation.HOW_IT_WORKS_BOTTOM}
					/>
				</PageSection>
			</InViewport>
			<InViewport height={461}>
				<MarketingNavSection variant="aqua" />
			</InViewport>
		</article>
	);
}

export const HowItWorksPage = withComponentErrorBoundary(HowItWorksPageComp, {
	ErrorFallback: ErrorPageSection,
});
